<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const SEGMENT_LENGTH_KM = 2

const route = useRoute()
const segmentTimes = ref([])
const segmentName = ref('')
const loading = ref(false)
const error = ref('')

const segmentId = computed(() => route.params.segmentId)

const sortedTimes = computed(() => {
  return [...segmentTimes.value].sort((a, b) => {
    const aDuration = Number(a.duration_ms)
    const bDuration = Number(b.duration_ms)

    if (!Number.isFinite(aDuration)) return 1
    if (!Number.isFinite(bDuration)) return -1
    return aDuration - bDuration
  })
})

async function fetchSegmentTimes() {
  if (!segmentId.value) return

  loading.value = true
  error.value = ''

  try {
    const response = await fetch(`https://api.sledzgps.pl/api/v1/segments/${segmentId.value}/times`)
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const data = await response.json()
    segmentName.value = data.segment_name || ''
    segmentTimes.value = Array.isArray(data.times) ? data.times : []
  } catch (fetchError) {
    console.error('Error fetching segment times:', fetchError)
    error.value = 'Nie udało się pobrać wyników segmentu.'
    segmentName.value = ''
    segmentTimes.value = []
  } finally {
    loading.value = false
  }
}

function formatDuration(durationMs) {
  const milliseconds = Number(durationMs)
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return '--'

  const totalSeconds = Math.floor(milliseconds / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = String(totalSeconds % 60).padStart(2, '0')
  return `${minutes}:${seconds}`
}

function formatAverageSpeed(durationMs) {
  const milliseconds = Number(durationMs)
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return '--'

  const speed = SEGMENT_LENGTH_KM / (milliseconds / (1000 * 60 * 60))
  return `${speed.toFixed(2)} km/h`
}

onMounted(fetchSegmentTimes)
watch(() => route.params.segmentId, fetchSegmentTimes)
</script>

<template>
  <div>
    <h1>Wyniki segmentu {{ segmentName ? `- ${segmentName}` : `#${segmentId}` }}</h1>

    <div class="table-scroll">
      <div class="settings">
        <span class="badge bg-info">Długość segmentu: {{ SEGMENT_LENGTH_KM }} km</span>
      </div>

      <div v-if="loading" class="alert alert-secondary mt-3 mb-0">
        Ładowanie...
      </div>
      <div v-else-if="error" class="alert alert-danger mt-3 mb-0">
        {{ error }}
      </div>
      <table v-else class="table table-striped">
        <thead>
          <tr>
            <th>#</th>
            <th>Nr</th>
            <th>Zawodnik</th>
            <th>Czas</th>
            <th>Śr. prędkość</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(time, index) in sortedTimes"
            :key="time.id"
          >
            <td>{{ index + 1 }}</td>
            <td><span class="badge bg-secondary">{{ time.runner_number }}</span></td>
            <td>{{ time.runner_name }}</td>
            <td>{{ formatDuration(time.duration_ms) }}</td>
            <td>{{ formatAverageSpeed(time.duration_ms) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.table {
  text-align: left;
}

.table-scroll {
  width: 100%;
  max-height: 80vh;
  overflow-x: auto;
  overflow-y: auto;
}

.settings {
  margin-bottom: 8px;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa;
}
</style>
